import { useState, useEffect } from 'react'
import { apiService } from '../services/apiService'

interface BusinessInfo {
  legal_name: string
  phone_number: string
  address_line1: string
  address_line2: string
  city: string
  state: string
  country: string
  postal_code: string
  business_type: 'canadian_corporation' | 'sole_proprietorship' | ''
  canadian_business_number: string
  shipping_agreement_signed: boolean
}

interface BusinessInfoSetupProps {
  onComplete: (businessInfo: BusinessInfo) => void
  onSkip?: () => void
  editMode?: boolean
}

export default function BusinessInfoSetup({ onComplete, onSkip, editMode = false }: BusinessInfoSetupProps) {
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    legal_name: '',
    phone_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    country: 'US',
    postal_code: '',
    business_type: '',
    canadian_business_number: '',
    shipping_agreement_signed: false
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadExistingBusinessInfo()
  }, [])

  const loadExistingBusinessInfo = async () => {
    try {
      const response = await apiService.getClientBusinessInfo()
      if (response.success && response.data) {
        setBusinessInfo(response.data)
        // Only auto-complete if not in edit mode
        if (!editMode) {
          onComplete(response.data)
          return
        }
      }
    } catch (err) {
      console.error('Error loading business info:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSaving(true)

    try {
      const response = await apiService.saveClientBusinessInfo(businessInfo)
      if (response.success) {
        onComplete(businessInfo)
      } else {
        setError(response.error || 'Failed to save business information')
      }
    } catch (err) {
      console.error('Error saving business info:', err)
      setError('Failed to save business information. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleInputChange = (field: keyof BusinessInfo, value: string | boolean) => {
    setBusinessInfo(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (loading) {
    return (
      <div className="card" style={{ maxWidth: '600px', margin: '20px auto' }}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading business information...
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ maxWidth: '600px', margin: '20px auto' }}>
      <h2 style={{ marginTop: 0, fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
        Business Information Setup
      </h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Please provide your legal business information. This will be used for shipment management and customs documentation.
      </p>

      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          color: '#721c24',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gap: '16px', marginBottom: '20px' }}>
          {/* Legal Business Name */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
              Legal Business Name <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              value={businessInfo.legal_name}
              onChange={(e) => handleInputChange('legal_name', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              placeholder="Your Company Name LLC"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
              Phone Number <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="tel"
              value={businessInfo.phone_number}
              onChange={(e) => handleInputChange('phone_number', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              placeholder="+1 (555) 123-4567"
            />
          </div>

          {/* Business Type */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
              Business Type <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="business_type"
                  value="canadian_corporation"
                  checked={businessInfo.business_type === 'canadian_corporation'}
                  onChange={(e) => handleInputChange('business_type', e.target.value)}
                  style={{ marginRight: '8px' }}
                  required
                />
                <span style={{ fontSize: '14px' }}>Canadian Corporation</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="business_type"
                  value="sole_proprietorship"
                  checked={businessInfo.business_type === 'sole_proprietorship'}
                  onChange={(e) => handleInputChange('business_type', e.target.value)}
                  style={{ marginRight: '8px' }}
                  required
                />
                <span style={{ fontSize: '14px' }}>Sole Proprietorship</span>
              </label>
            </div>
          </div>

          {/* Canadian Business Number - Only show for corporations */}
          {businessInfo.business_type === 'canadian_corporation' && (
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                Canadian Business Number (9 digits) <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="text"
                value={businessInfo.canadian_business_number}
                onChange={(e) => {
                  // Only allow numbers and limit to 9 digits
                  const value = e.target.value.replace(/\D/g, '').slice(0, 9)
                  handleInputChange('canadian_business_number', value)
                }}
                required={businessInfo.business_type === 'canadian_corporation'}
                maxLength={9}
                style={{
                  width: '200px',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                placeholder="123456789"
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Enter your 9-digit Canadian Business Number (without spaces or dashes)
              </div>
            </div>
          )}

          {/* Address Line 1 */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
              Address Line 1 <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              value={businessInfo.address_line1}
              onChange={(e) => handleInputChange('address_line1', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              placeholder="123 Business Street"
            />
          </div>

          {/* Address Line 2 */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
              Address Line 2
            </label>
            <input
              type="text"
              value={businessInfo.address_line2}
              onChange={(e) => handleInputChange('address_line2', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              placeholder="Suite 100 (optional)"
            />
          </div>

          {/* City, State, Country row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                City <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="text"
                value={businessInfo.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                placeholder="City"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                State/Province
              </label>
              <input
                type="text"
                value={businessInfo.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                placeholder="State"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                Country <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <select
                value={businessInfo.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="MX">Mexico</option>
                <option value="GB">United Kingdom</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="IT">Italy</option>
                <option value="ES">Spain</option>
                <option value="AU">Australia</option>
                <option value="JP">Japan</option>
              </select>
            </div>
          </div>

          {/* Postal Code */}
          <div style={{ maxWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
              Postal Code <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              value={businessInfo.postal_code}
              onChange={(e) => handleInputChange('postal_code', e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
              placeholder="12345"
            />
          </div>
        </div>

        {/* Shipping Agreement Section */}
        <div style={{ 
          marginTop: '32px', 
          padding: '20px', 
          border: '1px solid #ddd', 
          borderRadius: '8px', 
          backgroundColor: '#f9f9f9' 
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: '#333' 
          }}>
            Shipping Agreement <span style={{ color: '#dc3545' }}>*</span>
          </h3>
          
          <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
            Before we can start shipping on your behalf, you must review and sign our shipping agreement. 
            This agreement outlines the terms and conditions for our shipping services.
          </p>

          <div style={{ marginBottom: '16px' }}>
            <a 
              href="https://docuseal.com/d/Nqz8NGwbPTAxqU" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '12px 24px',
                backgroundColor: '#007bff',
                color: '#fff',
                textDecoration: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              View & Sign Shipping Agreement
            </a>
          </div>

          <div style={{ marginTop: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={businessInfo.shipping_agreement_signed}
                onChange={(e) => handleInputChange('shipping_agreement_signed', e.target.checked)}
                style={{ marginRight: '8px' }}
                required
              />
              <span style={{ fontSize: '14px' }}>
                I have reviewed and signed the shipping agreement linked above <span style={{ color: '#dc3545' }}>*</span>
              </span>
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              style={{
                padding: '12px 24px',
                border: '1px solid #6c757d',
                backgroundColor: '#fff',
                color: '#6c757d',
                borderRadius: '4px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Skip for Now
            </button>
          )}
          <button
            type="submit"
            disabled={saving || !businessInfo.shipping_agreement_signed}
            style={{
              padding: '12px 24px',
              border: 'none',
              backgroundColor: (saving || !businessInfo.shipping_agreement_signed) ? '#6c757d' : '#007bff',
              color: '#fff',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: (saving || !businessInfo.shipping_agreement_signed) ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Saving...' : !businessInfo.shipping_agreement_signed ? 'Sign Agreement to Continue' : 'Save Business Information'}
          </button>
        </div>
      </form>
    </div>
  )
}